import { Observable, Subject, of } from 'rxjs';
import { tap } from 'rxjs/operators';

interface CacheContent {
    expiry: number;
    value: any;
}

export class CacheService {
    private cache: Map<string, CacheContent> = new Map<string, CacheContent>();
    private inFlightObservables: Map<string, Subject<any>> = new Map<string, Subject<any>>();
    readonly DEFAULT_MAX_AGE: number = 86400000; // 24 hours in milliseconds
    readonly TEN_MINUTES: number = 600000; // 10 minutes in milliseconds
    private readonly STORAGE_PREFIX = 'cache_';

    constructor() {
        this.loadCacheFromStorage();
    }

    get(key: string, fallback?: Observable<any>, maxAge?: number): Observable<any> | Subject<any> {
        if (this.hasValidCachedValue(key)) {
            return of(this.cache.get(key).value);
        }

        if (!maxAge) {
            maxAge = this.DEFAULT_MAX_AGE;
        }

        if (this.inFlightObservables.has(key)) {
            return this.inFlightObservables.get(key);
        } else if (fallback && fallback instanceof Observable) {
            this.inFlightObservables.set(key, new Subject());
            return fallback.pipe(
                tap((value: any) => {
                    this.set(key, value, maxAge);
                }),
            );
        } else {
            return Observable.throw('Requested key is not available in Cache');
        }
    }

    set(key: string, value: any, maxAge: number = this.DEFAULT_MAX_AGE): void {
        const cacheContent = { value: value, expiry: Date.now() + maxAge }; // Set expiry time in milliseconds
        this.cache.set(key, cacheContent);
        this.persistToStorage(key, cacheContent);
        this.notifyInFlightObservers(key, value);
    }

    has(key: string): boolean {
        return this.cache.has(key);
    }

    clear(key: string): void {
        this.cache.delete(key);
        this.removeFromStorage(key);
    }

    clearAll(): void {
        this.cache.clear();
        const keys = Object.keys(localStorage);
        keys.forEach((key) => {
            if (key.startsWith(this.STORAGE_PREFIX)) {
                localStorage.removeItem(key);
            }
        });
    }

    private notifyInFlightObservers(key: string, value: any): void {
        if (this.inFlightObservables.has(key)) {
            const inFlight = this.inFlightObservables.get(key);
            const observersCount = inFlight.observers.length;

            if (observersCount) {
                inFlight.next(value);
            }

            inFlight.complete();
            this.inFlightObservables.delete(key);
        }
    }

    private hasValidCachedValue(key: string): boolean {
        if (this.cache.has(key)) {
            if (this.cache.get(key).expiry < Date.now()) {
                this.cache.delete(key);
                this.removeFromStorage(key);
                return false;
            }

            return true;
        } else {
            return false;
        }
    }

    private persistToStorage(key: string, content: CacheContent): void {
        try {
            const storageKey = this.STORAGE_PREFIX + key;
            localStorage.setItem(storageKey, JSON.stringify(content));
        } catch (error) {
            console.error('Failed to persist cache to localStorage:', error);
        }
    }

    private removeFromStorage(key: string): void {
        try {
            const storageKey = this.STORAGE_PREFIX + key;
            localStorage.removeItem(storageKey);
        } catch (error) {
            console.error('Failed to remove cache from localStorage:', error);
        }
    }

    private loadCacheFromStorage(): void {
        try {
            const keys = Object.keys(localStorage);
            keys.forEach((key) => {
                if (key.startsWith(this.STORAGE_PREFIX)) {
                    const cacheKey = key.substring(this.STORAGE_PREFIX.length);
                    const storedContent = localStorage.getItem(key);
                    if (storedContent) {
                        const cacheContent: CacheContent = JSON.parse(storedContent);
                        if (cacheContent.expiry > Date.now()) {
                            this.cache.set(cacheKey, cacheContent);
                        } else {
                            localStorage.removeItem(key);
                        }
                    }
                }
            });
        } catch (error) {
            console.error('Failed to load cache from localStorage:', error);
        }
    }
}
